import { BaseService } from './BaseService';
import type {
  AgentDefinitionDto,
  AgentDefinitionFunctionDto,
  CreateAgentRequest,
  UpdateAgentRequest,
  AgentFilters,
  AgentMarkdownFileDto,
  CreateAgentMarkdownFileRequest,
  UpdateAgentMarkdownFileRequest,
  CreateAgentDefinitionFunctionRequest,
  UpdateAgentDefinitionFunctionRequest,
} from '../types/agent';
import type { AgentKnowledgeFileDto } from '../types/knowledgeFile';
import type { PagedResult } from '../types/tenant';

/**
 * Agent service for handling agent definition operations.
 */
export class AgentService extends BaseService {
  /**
   * Gets a paginated list of agent definitions with optional filtering.
   */
  async getAgents(filters?: AgentFilters): Promise<PagedResult<AgentDefinitionDto>> {
    const queryParams = new URLSearchParams();
    if (filters?.pageNumber) queryParams.append('pageNumber', filters.pageNumber.toString());
    if (filters?.pageSize) queryParams.append('pageSize', filters.pageSize.toString());
    if (filters?.searchTerm) queryParams.append('searchTerm', filters.searchTerm);
    if (filters?.category) queryParams.append('category', filters.category);
    if (filters?.isSystem !== undefined) queryParams.append('isSystem', filters.isSystem.toString());
    if (filters?.isActive !== undefined) queryParams.append('isActive', filters.isActive.toString());

    const url = `/agents${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return this.get<PagedResult<AgentDefinitionDto>>(url);
  }

  /**
   * Gets an agent definition by ID.
   */
  async getAgent(id: string): Promise<AgentDefinitionDto> {
    return this.get<AgentDefinitionDto>(`/agents/${id}`);
  }

  /**
   * Gets an agent definition by slug.
   */
  async getAgentBySlug(slug: string): Promise<AgentDefinitionDto> {
    return this.get<AgentDefinitionDto>(`/agents/slug/${slug}`);
  }

  /**
   * Creates a new agent definition.
   */
  async createAgent(data: CreateAgentRequest): Promise<AgentDefinitionDto> {
    return this.post<AgentDefinitionDto>('/agents', data);
  }

  /**
   * Updates an existing agent definition.
   */
  async updateAgent(id: string, data: UpdateAgentRequest): Promise<AgentDefinitionDto> {
    return this.put<AgentDefinitionDto>(`/agents/${id}`, data);
  }

  /**
   * Deletes an agent definition.
   */
  async deleteAgent(id: string): Promise<void> {
    return this.delete<void>(`/agents/${id}`);
  }

  /**
   * Clones an agent definition.
   */
  async cloneAgent(id: string, name?: string): Promise<AgentDefinitionDto> {
    const url = name
      ? `/agents/${id}/clone?name=${encodeURIComponent(name)}`
      : `/agents/${id}/clone`;
    return this.post<AgentDefinitionDto>(url);
  }

  /** Gets all markdown files for an agent (File Search). */
  async getMarkdownFiles(agentId: string): Promise<AgentMarkdownFileDto[]> {
    return this.get<AgentMarkdownFileDto[]>(`/agents/${agentId}/markdown-files`);
  }

  /** Gets a single markdown file by id. */
  async getMarkdownFile(agentId: string, fileId: string): Promise<AgentMarkdownFileDto> {
    return this.get<AgentMarkdownFileDto>(`/agents/${agentId}/markdown-files/${fileId}`);
  }

  /** Creates a markdown file for an agent. */
  async createMarkdownFile(agentId: string, data: CreateAgentMarkdownFileRequest): Promise<AgentMarkdownFileDto> {
    return this.post<AgentMarkdownFileDto>(`/agents/${agentId}/markdown-files`, {
      ...data,
      sortOrder: data.sortOrder ?? 0,
    });
  }

  /** Updates a markdown file. */
  async updateMarkdownFile(agentId: string, fileId: string, data: UpdateAgentMarkdownFileRequest): Promise<AgentMarkdownFileDto> {
    return this.put<AgentMarkdownFileDto>(`/agents/${agentId}/markdown-files/${fileId}`, {
      ...data,
      sortOrder: data.sortOrder ?? 0,
    });
  }

  /** Deletes a markdown file. */
  async deleteMarkdownFile(agentId: string, fileId: string): Promise<void> {
    return this.delete<void>(`/agents/${agentId}/markdown-files/${fileId}`);
  }

  /** Gets all registered functions for an agent (OpenAI Assistants style). */
  async getFunctions(agentId: string): Promise<AgentDefinitionFunctionDto[]> {
    return this.get<AgentDefinitionFunctionDto[]>(`/agents/${agentId}/functions`);
  }

  /** Creates a registered function for an agent. */
  async createFunction(agentId: string, data: CreateAgentDefinitionFunctionRequest): Promise<AgentDefinitionFunctionDto> {
    return this.post<AgentDefinitionFunctionDto>(`/agents/${agentId}/functions`, {
      ...data,
      sortOrder: data.sortOrder ?? 0,
    });
  }

  /** Updates a registered function. */
  async updateFunction(
    agentId: string,
    functionId: string,
    data: UpdateAgentDefinitionFunctionRequest
  ): Promise<AgentDefinitionFunctionDto> {
    return this.put<AgentDefinitionFunctionDto>(`/agents/${agentId}/functions/${functionId}`, {
      ...data,
      sortOrder: data.sortOrder ?? 0,
    });
  }

  /** Deletes a registered function. */
  async deleteFunction(agentId: string, functionId: string): Promise<void> {
    return this.delete<void>(`/agents/${agentId}/functions/${functionId}`);
  }

  /** Attaches a system (default) tool to an agent. */
  async attachSystemTool(agentId: string, toolId: string): Promise<AgentDefinitionFunctionDto> {
    return this.post<AgentDefinitionFunctionDto>(`/agents/${agentId}/functions/attach-system-tool`, { toolId });
  }

  /** Attaches any active catalog tool to an agent (custom or default/system). */
  async attachCatalogTool(agentId: string, toolId: string): Promise<AgentDefinitionFunctionDto> {
    return this.post<AgentDefinitionFunctionDto>(`/agents/${agentId}/functions/attach-tool`, { toolId });
  }

  // --- Knowledge Files (RAG / File Search) ---

  /** Gets all knowledge files for an agent. */
  async getKnowledgeFiles(agentId: string): Promise<AgentKnowledgeFileDto[]> {
    return this.get<AgentKnowledgeFileDto[]>(`/agents/${agentId}/knowledge-files`);
  }

  /** Uploads a knowledge file for an agent. */
  async uploadKnowledgeFile(agentId: string, file: File): Promise<AgentKnowledgeFileDto> {
    const formData = new FormData();
    formData.append('file', file);
    return this.post<AgentKnowledgeFileDto>(`/agents/${agentId}/knowledge-files`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  /** Deletes a knowledge file. */
  async deleteKnowledgeFile(agentId: string, fileId: string): Promise<void> {
    return this.delete<void>(`/agents/${agentId}/knowledge-files/${fileId}`);
  }

  /** Force re-sync knowledge files to the current provider. */
  async syncKnowledgeFiles(agentId: string): Promise<void> {
    return this.post<void>(`/agents/${agentId}/knowledge-files/sync`);
  }
}

export const agentService = new AgentService();
